import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Archive, ChevronDown, PackagePlus, Pencil, Plus, RefreshCw, Upload } from "lucide-react";

import { archiveInventory, getInventory } from "src/api/inventory";
import { useAuth } from "src/providers/AuthProvider";
import CustomModal from "src/components/CustomModal";
import CustomConfirmDialog from "src/components/CustomConfirmDialog";
import AddProductModal from "src/components/modals/AddProductModal";
import EditProductModal from "src/components/modals/EditProductModal";
import ProductDetailModal from "src/components/modals/ProductDetailModal";
import UploadProductsModal from "src/components/modals/UploadProductsModal";
import UpdateInventoryModal from "src/components/modals/UpdateInventoryModal";
import ArchivedInventoryModal from "src/components/inventory/ArchivedInventoryModal";
import { Button } from "src/components/ui/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "src/components/ui/dropdown-menu";
import { BranchSelect, DataTable, PageHead, SearchField, StatusBadge, type Column, type RowsPerPageOption } from "src/components/common";
import { getCustomizationValue, renderCurrency, renderNumber, showSuccessToast } from "src/lib/utils";
import { stockStatusOf, type ProductRow } from "src/types/inventory";

const Inventory = () => {
	const { user } = useAuth();
	const queryClient = useQueryClient();
	const [searchParams, setSearchParams] = useSearchParams();

	const productLabel = getCustomizationValue(user, "inventory_label", true) || "Inventory";

	const search = searchParams.get("search") ?? undefined;
	const page = Number(searchParams.get("page") ?? 1);
	const limitParam = searchParams.get("limit") ?? "10";
	const limit: RowsPerPageOption = limitParam === "All" ? "All" : Number(limitParam);
	const entity = searchParams.get("entity") ?? "";
	const archivedOpen = searchParams.get("archived") === "1";

	const [addSingleOpen, setAddSingleOpen] = useState(false);
	const [bulkUploadOpen, setBulkUploadOpen] = useState(false);

	const setParam = (key: string, value: string | null) => {
		const next = new URLSearchParams(searchParams);
		if (value === null || value === "0") next.delete(key);
		else next.set(key, value);
		setSearchParams(next);
	};

	const { data, isLoading } = useQuery({
		queryKey: ["inventory", page, limit, search, entity],
		queryFn: () => getInventory({ page, limit: limit === "All" ? 9999 : limit, search, entity }),
	});

	const { mutate: archiveProduct } = useMutation(archiveInventory, {
		onSuccess: ({ message }) => {
			showSuccessToast(message || "Product archived successfully");
			queryClient.invalidateQueries(["inventory"]);
		},
	});

	const list = data?.data;
	const products = list?.data ?? [];
	const total = list?.total ?? products.length;

	const columns: Column<ProductRow>[] = [
		{ key: "code", header: "Item Code", cell: (row) => row.item_id ?? "—" },
		{
			key: "name",
			header: "Product",
			primary: true,
			cell: (row) => (
				<div className="tw-flex tw-items-center tw-gap-2.5">
					{row.image ? (
						<img src={row.image} alt="" className="tw-h-9 tw-w-9 tw-shrink-0 tw-rounded-lg tw-border tw-border-border tw-object-cover" />
					) : (
						<div className="tw-flex tw-h-9 tw-w-9 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-lg tw-bg-slate-tint tw-text-slate">
							<PackagePlus className="tw-h-4 tw-w-4" />
						</div>
					)}
					<span className="tw-font-medium tw-text-text">{row.name ?? "—"}</span>
				</div>
			),
		},
		{ key: "sku", header: "SKU", cell: (row) => row.sku ?? "—" },
		{ key: "qty", header: "Qty", cell: (row) => renderNumber(row.available_qty ?? 0) },
		{
			key: "status",
			header: "Status",
			// Derived per row from quantity (spec §2.3). The API exposes no status field and no
			// status filter, so this labels rows but can't drive a server-side filter.
			cell: (row) => <StatusBadge status={stockStatusOf(row.available_qty)} />,
		},
		{ key: "cost", header: "Cost Price", cell: (row) => renderCurrency(row.cost_price ?? 0) },
		{ key: "price", header: "Selling Price", cell: (row) => renderCurrency(row.price ?? 0) },
		{
			key: "actions",
			header: "",
			hideOnMobile: true,
			className: "tw-w-[170px] tw-text-right",
			cell: (row) => (
				<div className="tw-flex tw-justify-end tw-gap-1">
					<CustomModal
						title="Product Detail"
						size="normal"
						trigger={
							<button
								type="button"
								className="tw-rounded-lg tw-px-2 tw-py-1 tw-text-[12.5px] tw-font-semibold tw-text-teal hover:tw-bg-teal-tint"
							>
								Detail
							</button>
						}
					>
						<ProductDetailModal product={row} />
					</CustomModal>
					<CustomModal
						title="Edit Product"
						size="normal"
						trigger={
							<button
								type="button"
								className="tw-rounded-lg tw-px-2 tw-py-1 tw-text-[12.5px] tw-font-semibold tw-text-text-soft hover:tw-bg-bg hover:tw-text-text"
							>
								<Pencil className="tw-h-3.5 tw-w-3.5" />
								<span className="tw-sr-only">Edit</span>
							</button>
						}
					>
						<EditProductModal product={row} />
					</CustomModal>
					<CustomConfirmDialog
						trigger={
							<button
								type="button"
								className="tw-rounded-lg tw-px-2 tw-py-1 tw-text-[12.5px] tw-font-semibold tw-text-red hover:tw-bg-red-tint"
							>
								<Archive className="tw-h-3.5 tw-w-3.5" />
								<span className="tw-sr-only">Archive</span>
							</button>
						}
						mutateFn={() => archiveProduct({ uuid: row.uuid })}
					/>
				</div>
			),
		},
	];

	return (
		<div className="tw-space-y-1">
			<PageHead
				title={productLabel}
				subtitle={isLoading ? "Loading..." : `${total} ${total === 1 ? "product" : "products"}`}
				actions={
					<>
						<CustomModal
							title={`Update ${productLabel}`}
							trigger={
								<Button size="sm" variant="outline" className="tw-gap-1.5">
									<RefreshCw className="tw-h-4 tw-w-4" />
									Update {productLabel}
								</Button>
							}
						>
							<UpdateInventoryModal />
						</CustomModal>

						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<Button size="sm" className="tw-gap-1.5 tw-bg-teal tw-text-white hover:tw-bg-teal-dark">
									<Plus className="tw-h-4 tw-w-4" />
									New Product
									<ChevronDown className="tw-h-4 tw-w-4 tw-opacity-80" />
								</Button>
							</DropdownMenuTrigger>
							{/* onCloseAutoFocus is suppressed so the dropdown doesn't return focus to its trigger
							    after the dialog below has already focused its first field. */}
							<DropdownMenuContent align="end" className="tw-w-[210px]" onCloseAutoFocus={(event) => event.preventDefault()}>
								<DropdownMenuItem onSelect={() => setTimeout(() => setAddSingleOpen(true), 0)}>
									<PackagePlus />
									Add Single
								</DropdownMenuItem>
								<DropdownMenuItem onSelect={() => setTimeout(() => setBulkUploadOpen(true), 0)}>
									<Upload />
									Bulk Upload
								</DropdownMenuItem>
								<DropdownMenuSeparator />
								<DropdownMenuItem onSelect={() => setParam("archived", "1")}>
									<Archive />
									View Archived
								</DropdownMenuItem>
							</DropdownMenuContent>
						</DropdownMenu>
					</>
				}
			/>

			<CustomModal title="Add Single Product" open={addSingleOpen} onOpenChange={setAddSingleOpen}>
				<AddProductModal />
			</CustomModal>
			<CustomModal title="Upload Bulk" open={bulkUploadOpen} onOpenChange={setBulkUploadOpen}>
				<UploadProductsModal />
			</CustomModal>

			<div className="tw-flex tw-flex-wrap tw-items-center tw-justify-end tw-gap-3 tw-py-4">
				<SearchField placeholder="Search products..." />
				<BranchSelect value={entity} onChange={(value) => setParam("entity", value)} allLabel="All stores" />
			</div>

			<DataTable
				data={products}
				columns={columns}
				rowKey={(row) => row.uuid}
				loading={isLoading}
				totalRows={total}
				page={page}
				onPageChange={(next) => setParam("page", String(next))}
				rowsPerPage={limit}
				onRowsPerPageChange={(next) => {
					const params = new URLSearchParams(searchParams);
					params.set("limit", String(next));
					params.set("page", "1");
					setSearchParams(params);
				}}
				emptyTitle={search ? `No products match "${search}"` : "No products yet"}
				emptyDescription={
					search ? "Try a different search term." : "Add your first product to start tracking stock and raising invoices."
				}
				emptyAction={
					!search && (
						<CustomModal
							title="Add Single Product"
							trigger={
								<Button size="sm" className="tw-gap-1.5 tw-bg-teal tw-text-white hover:tw-bg-teal-dark">
									<Plus className="tw-h-4 tw-w-4" />
									Add Product
								</Button>
							}
						>
							<AddProductModal />
						</CustomModal>
					)
				}
			/>

			<ArchivedInventoryModal
				open={archivedOpen}
				onOpenChange={(open) => !open && setParam("archived", null)}
				productLabel={productLabel}
			/>
		</div>
	);
};

export default Inventory;
